import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContactProps {
  dialogOpen?: boolean;
  setDialogOpen?: (open: boolean) => void;
}

const Contact = ({ dialogOpen, setDialogOpen }: ContactProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const { toast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      if (error) throw error;

      toast({
        title: "Message sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      
      if (setDialogOpen) setDialogOpen(false);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">Contact Us</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+371 12345678"
              />
            </div>
            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your climbing gym project..."
                rows={4}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <section id="contact" className="py-16 bg-gradient-hero text-accent-foreground relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-72 h-72 border-2 border-primary-foreground rounded-full" />
        <div className="absolute bottom-20 left-10 w-96 h-96 border-2 border-primary-foreground transform rotate-45" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display mb-4 leading-tight">
            WANT TO BUILD YOUR<br />
            CLIMBING GYM?
          </h2>
          
          <p className="text-xl sm:text-2xl text-accent-foreground/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get in touch with us today. Whether you need a complete bouldering facility, 
            commercial climbing walls, or just expert consultation - we're here to help bring your vision to life.
          </p>

          <Button 
            onClick={() => setDialogOpen?.(true)} 
            size="lg" 
            className="mb-10 bg-gradient-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          >
            Contact Us
          </Button>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10 flex-wrap">
            <a 
              href="mailto:aleksandrs@falkorsgroup.com"
              className="flex items-center gap-3 text-accent-foreground hover:text-primary transition-colors group"
            >
              <div className="p-3 bg-primary/20 rounded-full group-hover:bg-primary/30 transition-colors">
                <Mail className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-lg font-semibold">aleksandrs@falkorsgroup.com</span>
            </a>
            
            <div className="hidden sm:block w-px h-12 bg-accent-foreground/20" />
            
            <a 
              href="tel:+37126615029"
              className="flex items-center gap-3 text-accent-foreground hover:text-primary transition-colors group"
            >
              <div className="p-3 bg-primary/20 rounded-full group-hover:bg-primary/30 transition-colors">
                <Phone className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="text-left">
                <div className="text-sm text-accent-foreground/70">Sales</div>
                <div className="text-lg font-semibold">+371 26615029</div>
              </div>
            </a>
            
            <div className="hidden sm:block w-px h-12 bg-accent-foreground/20" />
            
            <div className="flex items-center gap-3 text-accent-foreground">
              <div className="p-3 bg-primary/20 rounded-full">
                <MapPin className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="text-left">
                <div className="text-lg font-semibold">Ūnijas iela 14</div>
                <div className="text-sm text-accent-foreground/70">Riga, Latvia</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>
    </>
  );
};

export default Contact;